(() => {
  'use strict';
  const root = () => window.Shopify?.routes?.root || '/';
  let queue = Promise.resolve();
  const ownKeys = ['_cc_preorder_version', '_cc_preorder_variant', '_cc_preorder_eligible', '_cc_preorder_available', '_cc_eta_active', '_cc_eta_start', '_cc_eta_end', '_cc_captured_on'];
  function desiredRecords(lines) {
    return lines.map(line => {
      const m = line.meta;
      return {key: line.key, quantity: line.quantity, properties: {
        ...line.properties,
        _cc_preorder_version: '1',
        _cc_preorder_variant: String(line.id),
        _cc_preorder_eligible: String(m.eligible === true),
        _cc_preorder_available: String(Math.max(0, Number(m.available) || 0)),
        _cc_eta_active: String(m.active === true),
        _cc_eta_start: m.start || '',
        _cc_eta_end: m.end || '',
        _cc_captured_on: m.day
      }};
    });
  }
  async function request(url, options = {}) {
    const response = await fetch(url, {...options, credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(10000)});
    if (!response.ok) throw new Error('Order information could not be updated (' + response.status + ').');
    return response;
  }
  function parse(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector('template[data-cc-preorder-cart]');
    if (!node) throw new Error('Order information data is unavailable.');
    const lines = JSON.parse(node.content.textContent);
    if (!Array.isArray(lines)) throw new Error('Invalid order information data.');
    for (const line of lines) {
      line.properties = Array.isArray(line.properties) ? Object.fromEntries(line.properties) : (line.properties || {});
    }
    return lines;
  }
  async function perform(response) {
    const ids = Object.keys(response?.sections || {}).filter(id => id !== 'cc-preorder-data').slice(0, 4);
    const sectionUrl = new URL(root() + 'cart', window.location.origin);
    sectionUrl.searchParams.set('section_id', 'cc-preorder-data');
    let lines = parse(await (await request(sectionUrl.href)).text());
    let changed = false;
    let latest;
    const maximum = Math.max(4, lines.length * 3 + 2);
    for (let attempt = 0; attempt < maximum; attempt++) {
      const planned = desiredRecords(lines);
      const index = planned.findIndex((p, i) => ownKeys.some(key => String(lines[i].properties?.[key] ?? '') !== p.properties[key]));
      if (index === -1) {
        if (response && latest) {
          response.sections = latest.sections;
          if ('item_count' in response) Object.assign(response, latest);
        }
        return {changed, cart: latest};
      }
      const item = planned[index];
      latest = await (await request(root() + 'cart/change.js', {
        method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify({id: item.key, quantity: item.quantity, properties: item.properties,
          sections: ['cc-preorder-data', ...ids], sections_url: root() + 'cart'})
      })).json();
      if (latest.status) throw new Error(latest.description || 'Order information could not be saved.');
      changed = true;
      lines = parse(latest.sections?.['cc-preorder-data'] || '');
    }
    throw new Error('The cart changed while order information was being updated. Please try again.');
  }
  function sync(response) {
    const task = queue.catch(() => {}).then(() => perform(response));
    queue = task;
    return task;
  }
  function showError() {
    window.dispatchEvent(new CustomEvent('showAlert', {detail: {type: 'error', message: 'Please try checkout again. We could not update your order information.'}}));
  }
  const approved = new WeakSet();
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[data-cart-note-navigation-form]')) return;
    if (approved.has(form)) { approved.delete(form); return; }
    if (form.dataset.ccPreorderPending === 'true') {event.preventDefault(); event.stopImmediatePropagation(); return;}
    event.preventDefault(); event.stopImmediatePropagation();
    const submitter = event.submitter;
    form.dataset.ccPreorderPending = 'true';
    try {
      await sync();
      approved.add(form);
      form.requestSubmit(submitter?.isConnected ? submitter : undefined);
    } catch (_) {showError();}
    finally {delete form.dataset.ccPreorderPending;}
  }, true);
  document.addEventListener('click', async event => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const target = new URL(link.href, window.location.origin);
    if (target.origin !== window.location.origin || !/\/checkout\/?$/.test(target.pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    try {await sync(); window.location.assign(target.href);} catch (_) {showError();}
  }, true);
  window.CCPreorderRecord = {sync};
  if (/\/cart\/?$/.test(window.location.pathname)) {
    const controls = [...document.querySelectorAll('[data-cart-note-navigation-container], form[data-cart-note-navigation-form]')];
    const previous = controls.map(node => node.inert);
    controls.forEach(node => {node.inert = true;});
    sync().then(result => {
      if (result.changed) window.location.reload();
    }).catch(showError).finally(() => controls.forEach((node, index) => {node.inert = previous[index];}));
  }
})();
